import setDebug from 'debug';
import { adapter, getScriptTag } from '@seek/tealium-adapter';
import { events as tealiumEvents } from './event-constants';

import { getHeaderFooterContext } from '../headerFooterContext';
import { getSeekId } from 'src/utils/account-selection';
import { isLocalDev } from 'src/utils/environment';
import {
  BRAND_INFO,
  getBrandReportUrl,
  getCreateUrl,
  getEditSubpathUrl,
  getPreviewSubpathUrl,
} from 'src/utils/urls';
import { getLanguage, getLocale, getSite } from 'src/config';
import { type Country, getZoneFromSite } from '@seek/melways-sites';
import type { TrackingProps } from '@seek/tealium-adapter/lib-types/types';

const debug = setDebug('analytics');
const tealiumAdapter = adapter();

export const events = tealiumEvents;

const profile = (brand: string) => {
  if (brand === 'seek') return 'hirer';
  if (brand === 'jobstreet' || brand === 'jobsdb') return 'seakasia-hirer';
  return '';
};

export const getTealiumScriptTag = (isProd = false) =>
  getScriptTag({
    account: 'seek',
    profile: profile(
      BRAND_INFO[getLocale().split('-')[1].toLowerCase() as Country],
    ),
    environment: isProd ? 'prod' : 'qa',
  });

interface PageFields extends TrackingProps {
  isLoggedin: boolean | null;
  seekAdvertiserId?: string | undefined;
  userId: string | null;
  siteSubsection: string;
  siteSection: string;
  currentPage: string;
  siteCountry: string;
  siteLanguage: string;
  brand: string;
  zone: string;
}

export const getCommonPageFields = async (): Promise<PageFields> => {
  const currentPage = getPageName();
  const siteCountry = getLocale().split('-')[1].toLowerCase();
  const siteLanguage = getLanguage();
  const zone = getZoneFromSite(getSite());
  const brand = BRAND_INFO[siteCountry as Country] || 'seek';

  const common = {
    siteSubsection: 'branding',
    siteSection: 'account',
    currentPage,
    siteCountry,
    siteLanguage,
    zone,
    brand,
  };

  const fields = {
    ...common,
    userId: null,
    isLoggedin: null,
  };

  try {
    let userId = null;
    if (!isLocalDev()) {
      const context = await getHeaderFooterContext();
      userId = context?.user?.id;
    }

    if (currentPage === events.PREVIEW_BRAND) {
      return {
        ...fields,
        isLoggedin: false,
        userId,
      };
    }

    return {
      ...fields,
      isLoggedin: true,
      seekAdvertiserId: getSeekId(),
      userId,
    };
  } catch (error) {
    debug('Failed to fetch account info', error);
  }

  return fields;
};

const trackPageLoad = async (eventName: string) => {
  tealiumAdapter.view(eventName, await getCommonPageFields());
};

export const trackListPageLoad = () => {
  trackPageLoad(events.BM_PAGE_VIEWED);
};

export const trackEditPageLoad = () => {
  trackPageLoad(events.BM_EDIT_PAGE_VIEWED);
};

export const trackCreatePageLoad = () => {
  trackPageLoad(events.BM_CREATE_PAGE_VIEWED);
};

export const trackPreviewPageLoad = () => {
  trackPageLoad(events.BM_PREVIEW_PAGE_VIEWED);
};

export const trackBrandReportPageLoad = () => {
  trackPageLoad(events.BM_REPORT_PAGE_VIEWED);
};

interface LinkOptions {
  actionOrigin: string;
  hasLogo: boolean;
  hasCoverImage: boolean;
}

export interface DeleteAssetOptions {
  adLiveCount?: number | null;
  brandsTotal?: number | null;
  isDefaultAdvertiserBrand?: boolean | null;
  hasDeleteWarning?: boolean;
}

type LinkFunction = (
  eventName: string,
  options?: Partial<LinkOptions | DeleteAssetOptions>,
) => void;

export const link: LinkFunction = async (eventName, options = {}) => {
  debug('track link', { eventName, options });

  const commonFields = await getCommonPageFields();

  tealiumAdapter.link(eventName, {
    linkName: eventName,
    ...options,
    ...commonFields,
  });
};

export const linkMultiple: (
  eventNames: string[],
  linkName: string,
) => void = async (eventNames, linkName) => {
  const commonFields = await getCommonPageFields();

  tealiumAdapter.link(eventNames, {
    linkName,
    ...commonFields,
  });
};

function getPageName(): string {
  const path = window.location.pathname;

  if (path.startsWith(getCreateUrl())) {
    return events.CREATE_BRAND;
  } else if (path.startsWith(getEditSubpathUrl())) {
    return events.EDIT_BRAND;
  } else if (path.startsWith(getPreviewSubpathUrl())) {
    return events.PREVIEW_BRAND;
  } else if (path.startsWith(getBrandReportUrl())) {
    return events.BRAND_REPORT;
  }
  return events.BRAND_MANAGEMENT;
}
